import { useEffect, useRef, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Loader2, MapPin } from "lucide-react";
import { GmapsProvider } from "@/components/builder/gmaps";

/**
 * Campo di testo libero con suggerimenti Google Places (AutocompleteSuggestion,
 * stessa integrazione del builder «Crea il tuo itinerario»: stessa chiave, stessa
 * libreria, session token per non duplicare i costi). Il testo resta libero:
 * il cliente può digitare senza selezionare. onSelectPlace(place) riceve
 * { name, address, lat, lng } quando un suggerimento viene scelto.
 * Senza chiave configurata si comporta come un normale input (fallback elegante).
 */
const Inner = ({ value, onChange, onSelectPlace, placeholder, testId, bias = null, inputClass = "" }) => {
    const placesLib = useMapsLibrary("places");
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const sessionRef = useRef(null);
    const boxRef = useRef(null);
    const debRef = useRef(null);
    const skipRef = useRef(false); // dopo una selezione non riaprire i suggerimenti sul testo impostato

    useEffect(() => {
        const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    useEffect(() => {
        if (!placesLib?.AutocompleteSuggestion || (value || "").trim().length < 3 || skipRef.current) { setItems([]); return undefined; }
        if (!sessionRef.current) sessionRef.current = new placesLib.AutocompleteSessionToken();
        clearTimeout(debRef.current);
        debRef.current = setTimeout(async () => {
            try {
                const req = { input: value.trim(), sessionToken: sessionRef.current, includedRegionCodes: ["it"] };
                if (bias) req.locationBias = { center: bias, radius: 45000 };
                const { suggestions } = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(req);
                setItems((suggestions || []).filter((s) => s.placePrediction).slice(0, 6));
                setOpen(true);
            } catch {
                setItems([]);
            }
        }, 300);
        return () => clearTimeout(debRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, placesLib]);

    const choose = async (sugg) => {
        const pred = sugg.placePrediction;
        setLoading(true);
        try {
            const place = pred.toPlace();
            await place.fetchFields({ fields: ["location", "displayName", "formattedAddress"] });
            sessionRef.current = null; // chiude la sessione di fatturazione Places
            const name = typeof place.displayName === "string" ? place.displayName : (place.displayName?.text || pred.text?.text || "");
            const address = place.formattedAddress || name;
            skipRef.current = true;
            onChange(address);
            onSelectPlace?.({ name, address, lat: place.location.lat(), lng: place.location.lng() });
        } catch {
            // luogo non risolvibile: resta il testo digitato
        }
        setItems([]);
        setOpen(false);
        setLoading(false);
    };

    return (
        <div ref={boxRef} className="relative">
            <input
                data-testid={testId}
                value={value}
                onChange={(e) => { skipRef.current = false; onChange(e.target.value); }}
                onFocus={() => items.length > 0 && setOpen(true)}
                placeholder={placeholder}
                autoComplete="off"
                className={inputClass}
            />
            {loading && <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-amber-400" />}
            {open && items.length > 0 && (
                <div data-testid={`${testId}-suggestions`} className="absolute z-40 mt-2 w-full overflow-hidden rounded-xl border border-amber-500/25 bg-[#101011] shadow-2xl">
                    {items.map((s) => (
                        <button
                            type="button"
                            key={s.placePrediction.placeId}
                            data-testid={`${testId}-opt`}
                            onClick={() => choose(s)}
                            className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm text-stone-200 transition-colors hover:bg-amber-400/10"
                        >
                            <MapPin size={13} className="shrink-0 text-amber-500/80" />
                            <span className="truncate">{s.placePrediction.text?.text}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export const PlaceAutocompleteInput = (props) => (
    <GmapsProvider>
        <Inner {...props} />
    </GmapsProvider>
);
